import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useLocation } from 'react-router';
import { createLscSupportIssue } from '../../api';
import { useLeague } from '../../hooks/useLeague';
import {
  LSC_SUPPORT_ISSUE_CATEGORIES,
  LSC_SUPPORT_ISSUE_SEVERITIES,
  type LscSupportIssue,
  type LscSupportIssueCategory,
  type LscSupportIssueSeverity,
} from '../../types';
import {
  REPORT_ISSUE_MODAL_INTRO,
  REPORT_ISSUE_MODAL_TITLE,
  REPORT_ISSUE_SUBMIT_CTA,
  REPORT_ISSUE_SUCCESS_COPY,
  REPORT_ISSUE_SUCCESS_TITLE,
  buildSupportIssueCreatePayload,
  parseSupportIssueError,
  supportIssueCategoryLabel,
  supportIssueSeverityLabel,
} from '../../supportIssueCopy';
import type { ReportIssuePrefill } from './ReportIssueContext';

export interface ReportIssueModalProps {
  open: boolean;
  prefill?: ReportIssuePrefill | null;
  onClose: () => void;
}

export function ReportIssueModal({ open, prefill, onClose }: ReportIssueModalProps) {
  const location = useLocation();
  const leagueCtx = useLeague();
  const league = leagueCtx.league ?? null;

  const [category, setCategory] = useState<LscSupportIssueCategory>('other');
  const [severity, setSeverity] = useState<LscSupportIssueSeverity>('medium');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [actualBehavior, setActualBehavior] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submittedIssue, setSubmittedIssue] = useState<LscSupportIssue | null>(null);

  const leagueId = prefill?.leagueId ?? league?.id ?? null;
  const surface = prefill?.surface ?? 'lsc_shell';

  useEffect(() => {
    if (!open) return;
    setCategory(prefill?.category ?? 'other');
    setSeverity(prefill?.severity ?? 'medium');
    setTitle(prefill?.title ?? '');
    setDescription(prefill?.description ?? '');
    setStepsToReproduce('');
    setExpectedBehavior('');
    setActualBehavior('');
    setSubmitting(false);
    setErrorMessage(null);
    setSubmittedIssue(null);
  }, [open, prefill]);

  const categoryOptions = useMemo(
    () =>
      LSC_SUPPORT_ISSUE_CATEGORIES.map((value) => ({
        value,
        label: supportIssueCategoryLabel(value),
      })),
    []
  );

  const severityOptions = useMemo(
    () =>
      LSC_SUPPORT_ISSUE_SEVERITIES.map((value) => ({
        value,
        label: supportIssueSeverityLabel(value),
      })),
    []
  );

  if (!open) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting || submittedIssue) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const payload = buildSupportIssueCreatePayload({
        leagueId,
        surface,
        category,
        severity,
        title,
        description,
        stepsToReproduce,
        expectedBehavior,
        actualBehavior,
        route: location.pathname,
        league,
        errorCode: prefill?.errorCode,
      });
      const issue = await createLscSupportIssue(payload);
      setSubmittedIssue(issue);
    } catch (error) {
      setErrorMessage(parseSupportIssueError(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-issue-modal-title"
      data-testid="report-issue-modal"
    >
      <div className="card max-w-lg w-full relative max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-secondary-400 hover:text-white"
          aria-label="Close"
          data-testid="report-issue-modal-close"
        >
          <X className="h-5 w-5" />
        </button>

        {submittedIssue ? (
          <div className="space-y-3" data-testid="report-issue-success">
            <h2 id="report-issue-modal-title" className="text-lg font-semibold text-white">
              {REPORT_ISSUE_SUCCESS_TITLE}
            </h2>
            <p className="text-sm text-secondary-300">{REPORT_ISSUE_SUCCESS_COPY}</p>
            <p className="text-sm text-secondary-400" data-testid="report-issue-reference">
              Reference: <span className="text-secondary-200">{submittedIssue.issue_id}</span>
            </p>
            <div className="flex justify-end pt-2">
              <button type="button" className="btn-primary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <h2 id="report-issue-modal-title" className="text-lg font-semibold text-white mb-2">
              {REPORT_ISSUE_MODAL_TITLE}
            </h2>
            <p className="text-sm text-secondary-400 mb-4">{REPORT_ISSUE_MODAL_INTRO}</p>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="report-issue-form">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-secondary-400">What happened?</span>
                <input
                  className="input-field"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  maxLength={200}
                  disabled={submitting}
                  data-testid="report-issue-title"
                />
              </label>

              <label className="flex flex-col gap-1 text-sm">
                <span className="text-secondary-400">Tell us more</span>
                <textarea
                  className="input-field min-h-[96px]"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  maxLength={8000}
                  disabled={submitting}
                  data-testid="report-issue-description"
                />
              </label>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-secondary-400">Category</span>
                  <select
                    className="input-field"
                    value={category}
                    onChange={(e) => setCategory(e.target.value as LscSupportIssueCategory)}
                    disabled={submitting}
                    data-testid="report-issue-category"
                  >
                    {categoryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-secondary-400">Severity</span>
                  <select
                    className="input-field"
                    value={severity}
                    onChange={(e) => setSeverity(e.target.value as LscSupportIssueSeverity)}
                    disabled={submitting}
                    data-testid="report-issue-severity"
                  >
                    {severityOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="flex flex-col gap-1 text-sm">
                <span className="text-secondary-400">Steps to reproduce (optional)</span>
                <textarea
                  className="input-field min-h-[72px]"
                  value={stepsToReproduce}
                  onChange={(e) => setStepsToReproduce(e.target.value)}
                  maxLength={8000}
                  disabled={submitting}
                  data-testid="report-issue-steps"
                />
              </label>

              <details className="text-sm text-secondary-400">
                <summary className="cursor-pointer hover:text-secondary-200">
                  Expected vs actual behavior (optional)
                </summary>
                <div className="mt-3 space-y-3">
                  <label className="flex flex-col gap-1">
                    <span>Expected behavior</span>
                    <textarea
                      className="input-field min-h-[64px]"
                      value={expectedBehavior}
                      onChange={(e) => setExpectedBehavior(e.target.value)}
                      maxLength={8000}
                      disabled={submitting}
                      data-testid="report-issue-expected"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span>Actual behavior</span>
                    <textarea
                      className="input-field min-h-[64px]"
                      value={actualBehavior}
                      onChange={(e) => setActualBehavior(e.target.value)}
                      maxLength={8000}
                      disabled={submitting}
                      data-testid="report-issue-actual"
                    />
                  </label>
                </div>
              </details>

              {errorMessage ? (
                <p className="text-sm text-red-300" role="alert" data-testid="report-issue-error">
                  {errorMessage}
                </p>
              ) : null}

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting || !title.trim() || !description.trim()}
                  data-testid="report-issue-submit"
                >
                  {submitting ? 'Submitting…' : REPORT_ISSUE_SUBMIT_CTA}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default ReportIssueModal;
